import type { LastAddedItemMethod } from '@ori-events/basket-updated';
import * as BasketUpdatedEvent from '@ori-events/basket-updated';
import * as ToggleMenuFlyoutEvent from '@ori-events/toggle-menu-flyout';
import { styled } from '@ori-ui/mui';
import type { VFC } from 'react';
import { useEffect, useState } from 'react';
import { Transition, TransitionGroup } from 'react-transition-group';
import { v4 } from 'uuid';

import { isMinibagVisible } from '../helpers';
import type { BubbleProps } from './Bubble';
import { Bubble } from './Bubble';
import { bubbleHeight, bubbleDuration } from './constants';

interface BubbleInstanceProps extends BubbleProps {
  id: string;
}

const topNavHeightStyle = 'var(--top-nav-height, 0)';
const toolbarPositionStyle = 'var(--ecatalogue-toolbar-position, 0)';

const BasketBubbleWrapper = styled('div')<{
  ownerState: { isECatalogue: boolean };
}>(({ theme, ownerState }) => ({
  boxSizing: 'border-box',
  width: theme.spacing(64),
  marginTop: '10px',
  pointerEvents: 'none',
  position: 'fixed',
  top: 0,
  right: '15px',
  transform: `translate(0, ${topNavHeightStyle})`,
  transition: `transform var(--top-nav-height-animation), 0.3s ease-in-out`,
  ...(ownerState.isECatalogue && {
    transform: `translate(0, ${toolbarPositionStyle})`,
    transition: `transform var(--ecatalogue-toolbar-position-animation), 0.3s ease-in-out`,
  }),
  zIndex: 10000,

  [theme.breakpoints.up('sm')]: {
    right: '30px',
  },

  '& *': {
    boxSizing: 'border-box',
  },
}));

const AnimationWrapper = styled('div')<{
  ownerState: { state: string };
}>(({ ownerState }) => ({
  transition: 'opacity 750ms',
  opacity: 0,

  ...(ownerState.state === 'entered' && {
    opacity: 1,
  }),

  ...(ownerState.state === 'exiting' && {
    opacity: 0,
    transition: 'margin-top 300ms 950ms, opacity 1250ms',
    marginTop: `-${bubbleHeight + 10}px`,
  }),
}));

interface BubbleGroupProps {
  isECatalogue: boolean;
}

export const BubbleGroup: VFC<BubbleGroupProps> = ({ isECatalogue }) => {
  const [bubbles, setBubbles] = useState<BubbleInstanceProps[]>([]);

  const removeBasketBubble = (bubbleId: string) => {
    setBubbles((currentBubbles) =>
      currentBubbles.filter((currentBubble) => currentBubble.id !== bubbleId),
    );
  };

  const onToggleMenuFlyout = ((event: ToggleMenuFlyoutEvent.Event) => {
    const { menuType } = event.detail;
    if (menuType === ToggleMenuFlyoutEvent.MenuTypes.Minibag && isMinibagVisible()) {
      // hide all bubbles when minibag is being opened
      setBubbles([]);
    }
  }) as EventListener;

  const onBasketUpdate = ((event: BasketUpdatedEvent.Event) => {
    // don't show bubble if not add-to-basket update or if minibag is visible
    if (!event.detail.lastAddedItem || isMinibagVisible()) {
      return;
    }

    const { itemCode, method } = event.detail.lastAddedItem;
    const { shoppingBagItems, offers } = event.detail.shoppingBag;
    const offersItems = offers.reduce(
      (acc: BasketUpdatedEvent.ShoppingBagItem[], offer: BasketUpdatedEvent.ShoppingBagOffer) => [
        ...acc,
        ...offer.items,
      ],
      [],
    );

    // proper typing for method -- TODO
    const methodType = method as unknown as LastAddedItemMethod;

    const item =
      methodType === BasketUpdatedEvent.LastAddedItemMethod.BUY_FOR_MONEY
        ? shoppingBagItems.find((product) => product.itemCode === itemCode)
        : offersItems.find((product) => product.itemCode === itemCode);

    if (!item) {
      return;
    }

    const bubble: BubbleInstanceProps = {
      id: v4(),
      code: item.itemCode,
      name: item.itemDescription,
      price: item.totalPrice,
      quantity: item.requestedQuantity,
      image: item.itemImageSrc,
      points: item.totalRedeemedPoints,
      methodType,
    };
    setBubbles((currentBubbles) => [...currentBubbles, bubble]);
    setTimeout(() => removeBasketBubble(bubble.id), bubbleDuration);
  }) as EventListener;

  useEffect(() => {
    window.addEventListener(BasketUpdatedEvent.Name, onBasketUpdate);
    window.addEventListener(ToggleMenuFlyoutEvent.Name, onToggleMenuFlyout);

    return () => {
      window.removeEventListener(BasketUpdatedEvent.Name, onBasketUpdate);
      window.removeEventListener(ToggleMenuFlyoutEvent.Name, onToggleMenuFlyout);
    };
  });

  const ownerState = { isECatalogue };

  return (
    <BasketBubbleWrapper ownerState={ownerState}>
      <TransitionGroup>
        {bubbles.map(({ id, ...bubble }) => (
          <Transition
            key={id}
            timeout={{
              appear: 0,
              enter: 0,
              exit: 1250,
            }}
          >
            {(state) => {
              const bubbleOwnerState = { state };

              return (
                <AnimationWrapper key={id} ownerState={bubbleOwnerState}>
                  <Bubble {...bubble} key={id} />
                </AnimationWrapper>
              );
            }}
          </Transition>
        ))}
      </TransitionGroup>
    </BasketBubbleWrapper>
  );
};
